import axios from "axios";
import { useEffect, useState } from "react";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { money } from "../lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const shortMonth = m => new Date(`${m}-01`).toLocaleDateString("tr-TR", { month: "short", year: "2-digit" });
const compact = v => new Intl.NumberFormat("tr-TR", { notation: "compact" }).format(v);

const TICK = { fontSize: 11 };
const TICK_MUTED = { fontSize: 11, fill: "#94a3b8" };
const LEGEND_STYLE = { fontSize: 12 };
const DOT_SM = { r: 2 };
const DOT = { r: 3 };
const DOT_ACTIVE_SM = { r: 4 };
const DOT_ACTIVE = { r: 5 };
const GRID_STROKE = "#e7edf3";

export default function TrendChart({ month, refreshKey }) {
  const [data, setData] = useState([]);
  const [currency, setCurrency] = useState("TRY");
  const [showGross, setShowGross] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reports/trend?months=12&end=${month}`).then(r => setData(r.data.data)).catch(() => setData([]));
  }, [month, refreshKey]);

  const suffix = currency === "TRY" ? "_try" : "_usd";
  const rows = data.map(x => ({
    label: shortMonth(x.month), Gelir: x[`income${suffix}`], Gider: x[`expense${suffix}`], Net: x[`net${suffix}`],
    Ciro: x[`gross${suffix}`], margin: x.margin,
  }));
  const hasData = rows.some(r => r.Gelir || r.Gider);
  const withMargin = rows.filter(r => r.margin != null);
  const avgMargin = withMargin.length ? withMargin.reduce((s, r) => s + r.margin, 0) / withMargin.length : null;
  const lastMargin = withMargin.length ? withMargin[withMargin.length - 1].margin : null;

  const tooltip = (value, name, entry) => {
    if (name === "Ciro" && entry?.payload?.margin != null) return [`${money(value, currency)} · marj %${entry.payload.margin}`, name];
    return [money(value, currency), name];
  };

  return (
    <section className="panel chart-panel trend-panel" data-testid="trend-panel">
      <div className="panel-head">
        <div>
          <p className="eyebrow">12 AYLIK TREND</p>
          <h3>Gelir, gider ve net kar</h3>
          {avgMargin != null && (
            <p className="margin-line" data-testid="margin-summary">
              Komisyon marjı: son ay <b>%{lastMargin}</b> · 12 ay ort. <b>%{avgMargin.toFixed(2)}</b>
            </p>
          )}
        </div>
        <div className="trend-controls">
          <button type="button" className={`toggle-chip ${showGross ? "on" : ""}`} data-testid="trend-gross-toggle" onClick={() => setShowGross(v => !v)}>
            Ciroyu göster
          </button>
          <div className="segmented mini" data-testid="trend-currency-toggle">
            <button type="button" className={currency === "TRY" ? "selected" : ""} data-testid="trend-currency-try" onClick={() => setCurrency("TRY")}>TL</button>
            <button type="button" className={currency === "USD" ? "selected" : ""} data-testid="trend-currency-usd" onClick={() => setCurrency("USD")}>USD</button>
          </div>
        </div>
      </div>
      <div className="chart-wrap" data-testid="trend-chart">
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ left: 8, right: showGross ? 8 : 16, top: 8 }}>
              <CartesianGrid vertical={false} stroke={GRID_STROKE}/>
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={TICK}/>
              <YAxis yAxisId="main" tickLine={false} axisLine={false} tick={TICK} tickFormatter={compact}/>
              {showGross && <YAxis yAxisId="gross" orientation="right" tickLine={false} axisLine={false} tick={TICK_MUTED} tickFormatter={compact}/>}
              <Tooltip formatter={tooltip}/>
              <Legend iconType="circle" wrapperStyle={LEGEND_STYLE}/>
              {showGross && <Line yAxisId="gross" type="monotone" dataKey="Ciro" stroke="#94a3b8" strokeWidth={2} strokeDasharray="3 3" dot={DOT_SM} activeDot={DOT_ACTIVE_SM}/>}
              <Line yAxisId="main" type="monotone" dataKey="Gelir" stroke="#10b981" strokeWidth={2.5} dot={DOT} activeDot={DOT_ACTIVE}/>
              <Line yAxisId="main" type="monotone" dataKey="Gider" stroke="#f59e0b" strokeWidth={2.5} dot={DOT} activeDot={DOT_ACTIVE}/>
              <Line yAxisId="main" type="monotone" dataKey="Net" stroke="#93257e" strokeWidth={2.5} strokeDasharray="6 4" dot={DOT} activeDot={DOT_ACTIVE}/>
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="empty-chart">Son 12 ay için veri yok</div>
        )}
      </div>
    </section>
  );
}
